/**
 * The event catalog. See ARCHITECTURE.md section 7.
 *
 * This module is the single source of truth for event type names; ARCHITECTURE.md 7.5 is its
 * documented mirror. Adding an event here without adding it there is a documentation defect.
 */
import { z } from 'zod';
export const EVENT_TYPES = [
    // Reservations (Phase 4)
    'RESERVATION_CREATED',
    'RESERVATION_CANCELLED',
    'RESERVATION_MODIFIED',
    // Guest experience (Phase 8)
    'GUEST_MESSAGE_RECEIVED',
    'NEGATIVE_SENTIMENT_DETECTED',
    // Maintenance (Phase 7)
    'MAINTENANCE_ISSUE_CREATED',
    'MAINTENANCE_ISSUE_OVERDUE',
    'MAINTENANCE_ISSUE_RESOLVED',
    // Finance (Phases 3 and 5)
    'TRANSACTION_IMPORTED',
    'TRANSACTION_CLASSIFIED',
    'TRANSACTION_ANOMALY_DETECTED',
    // PERIOD_CLOSE_* were removed 2026-09-17 (ADR-0007): QuickBooks closes periods, Sixgen does not.
    'LARGE_EXPENSE_DETECTED',
    'CASH_RESERVE_THRESHOLD_BREACHED',
    // Revenue (Phase 4)
    'REVENUE_BELOW_FORECAST',
    // Acquisitions (Phase 10, deferred to a later version — the event is the interface that stays)
    'NEW_ACQUISITION_ADDED',
    // Platform (Phases 1 and 2)
    'OWNER_APPROVAL_REQUIRED',
    'APPROVAL_REQUESTED',
    'APPROVAL_DECIDED',
    'AGENT_RUN_FAILED',
    'INTEGRATION_AUTH_EXPIRED',
    'KILL_SWITCH_CHANGED',
    'ENTITY_CREATED',
    'PROPERTY_CREATED',
    'PROPERTY_UPDATED',
    'SYSTEM_HEARTBEAT',
];
export const eventTypeSchema = z.enum(EVENT_TYPES);
/**
 * Which phase introduces each event. Publishing an event whose phase has not landed is a
 * build-ahead violation (CLAUDE.md 4.1); `assertEventAvailable` enforces it at runtime.
 */
export const EVENT_PHASE = {
    // `core.entity` landed in Phase 1 with the rest of core; only the write path arrived late.
    ENTITY_CREATED: 1,
    PROPERTY_CREATED: 1,
    PROPERTY_UPDATED: 1,
    SYSTEM_HEARTBEAT: 1,
    KILL_SWITCH_CHANGED: 1,
    AGENT_RUN_FAILED: 2,
    APPROVAL_REQUESTED: 2,
    APPROVAL_DECIDED: 2,
    OWNER_APPROVAL_REQUIRED: 2,
    INTEGRATION_AUTH_EXPIRED: 3,
    TRANSACTION_IMPORTED: 3,
    TRANSACTION_CLASSIFIED: 3,
    TRANSACTION_ANOMALY_DETECTED: 3,
    LARGE_EXPENSE_DETECTED: 3,
    RESERVATION_CREATED: 4,
    RESERVATION_CANCELLED: 4,
    RESERVATION_MODIFIED: 4,
    REVENUE_BELOW_FORECAST: 4,
    CASH_RESERVE_THRESHOLD_BREACHED: 5,
    MAINTENANCE_ISSUE_CREATED: 7,
    MAINTENANCE_ISSUE_OVERDUE: 7,
    MAINTENANCE_ISSUE_RESOLVED: 7,
    GUEST_MESSAGE_RECEIVED: 8,
    NEGATIVE_SENTIMENT_DETECTED: 8,
    NEW_ACQUISITION_ADDED: 10,
};
/**
 * The phase currently being built. Bump this in the phase that starts, not before.
 *
 * 9 since 2026-09-23: Phase 3 closed, Phase 4 had closed, and Phase 9 — the read-only production
 * cutover — started that day (ROADMAP §1). It had stayed at 4 through Phase 3's close, which was
 * a stale constant rather than a claim. Because ROADMAP §2 runs Phase 9 before Phase 5, the
 * constant is 9 while Phase 5's own event, `CASH_RESERVE_THRESHOLD_BREACHED`, is published by
 * the Phase 5 groundwork built under the ROADMAP §6 decision of the same date; the gate is
 * "no event from a phase later than the one in progress", and 5 is not later than 9.
 */
export const CURRENT_PHASE = 9;
export function assertEventAvailable(type, currentPhase = CURRENT_PHASE) {
    const phase = EVENT_PHASE[type];
    if (phase > currentPhase) {
        throw new Error(`Event ${type} belongs to Phase ${phase}; current phase is ${currentPhase}. ` +
            'Publishing it now would build ahead (CLAUDE.md 4.1).');
    }
}
/** A reference to the row an event is about. */
export const entityRefSchema = z.object({
    table: z.string().min(1),
    id: z.string().min(1),
});
/** What a caller supplies to publish an event. The outbox fills in the rest. */
export const newEventSchema = z.object({
    type: eventTypeSchema,
    entityRef: entityRefSchema.nullish(),
    payload: z.record(z.unknown()).default({}),
    /**
     * Idempotency key. ARCHITECTURE.md 7.4: type + source_record_id + content_hash.
     * Build it with `idempotencyKey`. A second insert with the same key is a no-op.
     */
    idempotencyKey: z.string().min(1),
    causedByRunId: z.string().uuid().nullish(),
    causedByEventId: z.string().uuid().nullish(),
});
export function idempotencyKey(type, sourceRecordId, contentHash) {
    return `${type}:${sourceRecordId}:${contentHash}`;
}
/** Loop protection. ARCHITECTURE.md 7.4. */
export const DEFAULT_MAX_CHAIN_DEPTH = 5;
//# sourceMappingURL=events.js.map